/**
 * La colección.
 *
 * Se agrupa por carta y no se listan los ejemplares sueltos. Con 20 cartas de
 * salida y un catálogo de 12, una rejilla plana serían diez filas del mismo
 * Escudero: el jugador no quiere ver sus copias, quiere ver *qué tiene* y cuánto
 * le falta para mejorarlo.
 *
 * Dentro de cada carta los ejemplares se separan por nivel, porque solo se
 * pueden fusionar tres del mismo nivel: agruparlos todos juntos escondería
 * justamente el dato que hace falta para decidir.
 */

import { AnimatePresence, motion } from 'framer-motion';
import { Layers, Loader2, Sparkles } from 'lucide-react';
import { useMemo, useState } from 'react';
import { CardView } from '@/components/cards/CardView';
import { Button } from '@/components/ui/Button';
import { CARD_POOL, getCardMaster } from '@/data';
import { DeckEditor } from '@/components/collection/DeckEditor';
import { useFuseCards } from '@/hooks/useCollectionActions';
import type { CollectionCard, Deck, Profile } from '@/types/game';

/** Coste en oro de subir del nivel indicado al siguiente. Espeja fuse_user_cards. */
const FUSION_COST_PER_LEVEL = 100;
const CARDS_PER_FUSION = 3;
const MAX_LEVEL = 5;

interface CollectionScreenProps {
  cards: readonly CollectionCard[];
  profile: Profile | null;
  deck: Deck | null;
  userId: string | undefined;
  isLoading: boolean;
  /** Modo local: no hay cuenta ni colección persistente. */
  isOffline: boolean;
}

type Tab = 'cards' | 'deck';

/** Un montón de ejemplares idénticos: misma carta, mismo nivel. */
interface Stack {
  masterId: string;
  level: number;
  cards: CollectionCard[];
}

function buildStacks(cards: readonly CollectionCard[]): Map<string, Stack[]> {
  const byMaster = new Map<string, Map<number, CollectionCard[]>>();

  for (const card of cards) {
    const levels = byMaster.get(card.masterId) ?? new Map<number, CollectionCard[]>();
    const same = levels.get(card.level) ?? [];
    same.push(card);
    levels.set(card.level, same);
    byMaster.set(card.masterId, levels);
  }

  const result = new Map<string, Stack[]>();
  for (const [masterId, levels] of byMaster) {
    result.set(
      masterId,
      [...levels.entries()]
        .map(([level, group]) => ({ masterId, level, cards: group }))
        .sort((a, b) => b.level - a.level),
    );
  }
  return result;
}

function StackRow({
  stack,
  gold,
  onFuse,
  isFusing,
}: {
  stack: Stack;
  gold: number;
  onFuse: (ids: string[]) => void;
  isFusing: boolean;
}) {
  const cost = FUSION_COST_PER_LEVEL * stack.level;
  const canFuse =
    stack.cards.length >= CARDS_PER_FUSION && stack.level < MAX_LEVEL && gold >= cost;

  // Se ordenan por número de serie para que fusionar consuma primero los más
  // altos: un coleccionista quiere conservar el #3, no perderlo por sorteo.
  const ordered = [...stack.cards].sort(
    (a, b) => (b.serialNumber ?? Number.MAX_SAFE_INTEGER) - (a.serialNumber ?? Number.MAX_SAFE_INTEGER),
  );

  const reason =
    stack.level >= MAX_LEVEL
      ? 'Nivel máximo'
      : stack.cards.length < CARDS_PER_FUSION
        ? `Te faltan ${CARDS_PER_FUSION - stack.cards.length} para fusionar`
        : gold < cost
          ? `Necesitas ${cost} de oro`
          : null;

  return (
    <div className="flex items-center justify-between gap-2 rounded-lg bg-slate-800/50 px-2 py-1.5">
      <div className="flex items-center gap-2">
        <span className="rounded-md bg-slate-900 px-1.5 py-0.5 text-[11px] font-bold text-amber-200">
          Nivel {stack.level}
        </span>
        <span className="text-xs text-slate-400">×{stack.cards.length}</span>
        {/* Los números de serie son la razón de ser de una carta limitada:
            merecen verse, no esconderse tras un menú. */}
        {ordered.some((card) => card.serialNumber !== null) ? (
          <span className="truncate font-mono text-[10px] text-slate-500">
            {ordered
              .filter((card) => card.serialNumber !== null)
              .slice(0, 3)
              .map((card) => `#${card.serialNumber}`)
              .join(' ')}
          </span>
        ) : null}
      </div>

      {canFuse ? (
        <Button
          size="sm"
          variant="secondary"
          disabled={isFusing}
          onClick={() => onFuse(ordered.slice(0, CARDS_PER_FUSION).map((card) => card.id))}
        >
          <Sparkles className="h-3 w-3" aria-hidden />
          Fusionar · {cost}
        </Button>
      ) : (
        <span className="text-[10px] text-slate-500">{reason}</span>
      )}
    </div>
  );
}

export function CollectionScreen({
  cards,
  profile,
  deck,
  userId,
  isLoading,
  isOffline,
}: CollectionScreenProps) {
  const stacksByMaster = useMemo(() => buildStacks(cards), [cards]);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('cards');
  const fuse = useFuseCards(userId);

  if (isOffline) {
    return (
      <div className="grid flex-1 place-items-center p-6 text-center">
        <div className="max-w-xs">
          <Layers className="mx-auto h-8 w-8 text-slate-600" aria-hidden />
          <p className="mt-3 text-sm text-slate-400">
            La colección necesita cuenta. En modo local se juega con el mazo inicial.
          </p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="grid flex-1 place-items-center gap-2 text-slate-400">
        <Loader2 className="h-6 w-6 animate-spin" aria-hidden />
        <p className="text-sm">Cargando la colección…</p>
      </div>
    );
  }

  const masters = [...stacksByMaster.keys()].sort((a, b) => {
    const left = getCardMaster(a);
    const right = getCardMaster(b);
    return left.cost - right.cost || left.name.localeCompare(right.name, 'es');
  });

  const handleFuse = (ids: string[]) => {
    setError(null);
    fuse.mutate(ids, { onError: (mutationError) => setError(mutationError.message) });
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain p-2 sm:p-4">
      <div className="mx-auto w-full max-w-4xl">
        <div className="mb-3 flex items-baseline justify-between gap-2">
          <div className="flex gap-1">
            {(
              [
                ['cards', 'Colección'],
                ['deck', 'Mazo'],
              ] as const
            ).map(([id, label]) => (
              <button
                key={id}
                type="button"
                onClick={() => setTab(id)}
                className={`rounded-md px-2.5 py-1 text-sm font-semibold transition ${
                  tab === id ? 'bg-white/15 text-white' : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <p className="shrink-0 text-xs text-slate-400">
            {cards.length} ejemplares · {masters.length}/{CARD_POOL.length}
          </p>
        </div>

        {tab === 'deck' ? (
          <DeckEditor deck={deck} inventory={cards} userId={userId} />
        ) : (
          <>
            <AnimatePresence>
              {error ? (
                <motion.p
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  role="alert"
                  className="mb-3 rounded-lg bg-rose-950/70 px-3 py-2 text-xs text-rose-200 ring-1 ring-rose-500/40"
                >
                  {error}
                </motion.p>
              ) : null}
            </AnimatePresence>

            {masters.length === 0 ? (
          <p className="py-10 text-center text-sm text-slate-500">
            Todavía no tienes cartas. Abre un sobre en la tienda.
          </p>
        ) : (
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-5 sm:gap-3 lg:grid-cols-6">
            {masters.map((masterId) => {
              const stacks = stacksByMaster.get(masterId) ?? [];
              const total = stacks.reduce((sum, stack) => sum + stack.cards.length, 0);
              const best = stacks[0];
              const isOpen = expanded === masterId;

              return (
                <div key={masterId} className={isOpen ? 'col-span-3 sm:col-span-5 lg:col-span-6' : ''}>
                  <div className={isOpen ? 'flex gap-3 rounded-xl bg-slate-900/60 p-2' : ''}>
                    <button
                      type="button"
                      onClick={() => setExpanded(isOpen ? null : masterId)}
                      className={`relative block ${isOpen ? 'w-24 shrink-0' : 'w-full'}`}
                      aria-expanded={isOpen}
                    >
                      {best ? (
                        <CardView
                          card={{
                            instanceId: masterId,
                            masterId,
                            level: best.level,
                            owner: 'PLAYER',
                          }}
                          variant="hand"
                        />
                      ) : null}
                      {total > 1 ? (
                        <span className="absolute -bottom-1 -left-1 rounded-full bg-slate-950 px-1.5 text-[10px] font-bold text-slate-200 ring-1 ring-white/20">
                          ×{total}
                        </span>
                      ) : null}
                    </button>

                    {isOpen ? (
                      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                        <p className="text-sm font-semibold text-white">
                          {getCardMaster(masterId).name}
                        </p>
                        {stacks.map((stack) => (
                          <StackRow
                            key={stack.level}
                            stack={stack}
                            gold={profile?.gold ?? 0}
                            onFuse={handleFuse}
                            isFusing={fuse.isPending}
                          />
                        ))}
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
